import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Car, MapPin, Users, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function EventCarpool({ eventId, isOwner }) {
  const [carpools, setCarpools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    driver_name: "",
    driver_phone: "",
    departure_location: "",
    departure_time: "",
    available_seats: 1
  });

  useEffect(() => {
    fetchCarpools();
  }, [eventId]);

  const fetchCarpools = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/carpool`);
      setCarpools(response.data);
    } catch (error) {
      console.error("Failed to fetch carpools:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.driver_name || !formData.departure_location) {
      toast.error("Name und Abfahrtsort sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/carpool`,
        formData
      );
      
      toast.success("Mitfahrgelegenheit hinzugefügt!");
      setFormData({ 
        driver_name: "", 
        driver_phone: "", 
        departure_location: "", 
        departure_time: "",
        available_seats: 1 
      });
      fetchCarpools();
    } catch (error) {
      console.error("Failed to add carpool:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleJoin = async (carpoolId) => {
    const name = prompt("Dein Name:");
    if (!name) return;

    try {
      await axios.post(
        `${API}/events/${eventId}/carpool/${carpoolId}/join`,
        { passenger_name: name }
      );
      toast.success("Du bist dabei!");
      fetchCarpools();
    } catch (error) {
      console.error("Failed to join carpool:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Beitreten");
    }
  };

  const handleDelete = async (carpoolId) => {
    if (!confirm("Mitfahrgelegenheit wirklich löschen?")) return;
    
    try {
      await axios.delete(
        `${API}/events/${eventId}/carpool/${carpoolId}`,
        { withCredentials: true }
      );
      toast.success("Gelöscht");
      fetchCarpools();
    } catch (error) {
      console.error("Failed to delete carpool:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Car className="w-5 h-5 text-primary" />
          Mitfahrgelegenheiten
        </h3>
        <p className="text-sm text-muted-foreground">
          Biete eine Mitfahrgelegenheit an oder finde eine!
        </p>
      </div>

      {/* Add Carpool Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Dein Name *</Label>
            <Input
              value={formData.driver_name}
              onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
              placeholder="Fahrer"
              required
            />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input
              value={formData.driver_phone}
              onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
              placeholder="+49..."
            />
          </div>
        </div>

        <div>
          <Label>Abfahrtsort *</Label>
          <Input
            value={formData.departure_location}
            onChange={(e) => setFormData({ ...formData, departure_location: e.target.value })}
            placeholder="z.B. München Hauptbahnhof"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Abfahrtszeit</Label>
            <Input
              type="time"
              value={formData.departure_time}
              onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })}
            />
          </div>
          <div>
            <Label>Freie Plätze</Label>
            <Input
              type="number"
              min="1"
              value={formData.available_seats}
              onChange={(e) => setFormData({ ...formData, available_seats: parseInt(e.target.value) || 1 })}
            />
          </div>
        </div>

        <Button type="submit" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Mitfahrgelegenheit anbieten
        </Button>
      </form>

      {/* Carpools List */}
      <div className="space-y-4">
        {carpools.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Car className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch keine Mitfahrgelegenheiten</p>
            <p className="text-xs mt-1">Biete die erste an!</p>
          </div>
        ) : (
          carpools.map((carpool) => {
            const seatsLeft = carpool.available_seats - (carpool.passengers?.length || 0);
            
            return (
              <div key={carpool.id} className="p-4 bg-secondary/20 rounded-lg border">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Car className="w-4 h-4 text-primary" />
                      <span className="font-medium">{carpool.driver_name}</span>
                      {carpool.driver_phone && (
                        <span className="text-xs text-muted-foreground">📞 {carpool.driver_phone}</span>
                      )}
                    </div>

                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        {carpool.departure_location}
                      </div>
                      {carpool.departure_time && (
                        <div className="text-muted-foreground">
                          🕐 {carpool.departure_time} Uhr
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Users className="w-3 h-3" />
                        <span className={seatsLeft > 0 ? 'text-green-600 font-medium' : 'text-muted-foreground'}>
                          {seatsLeft > 0 ? `${seatsLeft} Plätze frei` : 'Voll'}
                        </span>
                      </div>
                    </div>

                    {carpool.passengers && carpool.passengers.length > 0 && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-1">Mitfahrer:</p>
                        <div className="flex flex-wrap gap-1">
                          {carpool.passengers.map((p, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-primary/10 rounded-full text-xs">
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {seatsLeft > 0 && (
                      <Button
                        size="sm"
                        onClick={() => handleJoin(carpool.id)}
                        className="whitespace-nowrap"
                      >
                        Mitfahren
                      </Button>
                    )}
                    {isOwner && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(carpool.id)}
                        className="h-8 w-8 text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}